/* eslint-disable max-lines */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getCartQuery } from '@/api/booking/getCartQuery';
import { getProfessionalsQuery } from '@/api/booking/getProfessionalsQuery';
import type { ProfessionalsResponse } from '@/api/booking/getProfessionalsQuery/types';
import type { UpsertCartPayload } from '@/api/booking/upsertCartMutation';
import { upserCartMutation } from '@/api/booking/upsertCartMutation';
import type {
  CartCustomerPayload,
  CartCustomerWithLanguage,
  PaymentPayload,
} from '@/api/booking/upsertCartMutation/types';
import {
  MEMBERSHIP_PROMO_CONFLICT_CODE,
  validateSale,
} from '@/api/booking/validateSale';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { useToast } from '@/components/_shared/Toaster';
import type { UpsertCartResponse } from '@/hooks/mutations/booking/useUpsertCartMutation';
import type {
  Cart,
  CartItem,
  SelectedProfessional,
  SelectedService,
  Service,
  Tip,
} from '@/types/booking';
import { isNoResponse, isSlotUnavailableError } from '@/utils/apiErrors';
import {
  transformCartIntoPayloadCart,
  validateCartItems,
} from '@/utils/bookingCartUtils';
import { Logger } from '@/utils/logging';
import { Tracker } from '@/utils/tracking';

import { useSessionCustomerId } from '../auth/useSessionCustomerId';
import { useCustomerQuery } from '../queries/customer/useCustomerQuery';
import { useErrorTranslations } from '../useErrorTranslations';

/**
 * Shape of an unconfigured guest slot for group appointments — the subset of
 * CartItem fields available before a barber/services are chosen. Matches the
 * fields the upsert path (syncCartWithServer) accepts, so the placeholder is
 * structurally validated at its declaration site.
 */
type EmptyCartItem = Pick<
  CartItem,
  | 'id'
  | 'name'
  | 'services'
  | 'bookedWithAnyBarber'
  | 'bookedFromWaitingList'
  | 'barberId'
  | 'barberPasscode'
  | 'dateTime'
  | 'tipAmount'
  | 'tipPercentage'
>;

/**
 * Detects whether a cart-mutation error is the server rejecting the promo
 * (rather than a genuine cart failure like a closed shop or invalid service).
 *
 * Typed promo failures carry a `promo.errors.*` code (or `ResourceNotFound`
 * for a missing promo). The referral-eligibility check, however, throws a
 * *generic* `BadRequestError` whose only promo signal is the message string.
 *
 * Misclassifying here is costly: a false positive permanently drops the user's
 * (possibly valid) promo for the session and strips it from the URL. So the
 * untyped branch is deliberately narrow — it requires `code === 'BadRequest'`
 * AND eligibility wording, rather than any message that merely mentions a
 * "promo". This avoids matching unrelated cart errors like "Promotional period
 * ended" or "promo adjustment pending". The eligibility message is localized by
 * the API (`promo.errors.notEligibleForPromotion`), so we match the eligibility
 * stem in both English ("eligible") and Spanish ("elegible"). Genuine non-promo
 * errors fall through and surface to the user unchanged.
 *
 * Once the API returns a typed `promo.errors.notValidForCustomer` for this
 * case, the `BadRequest` branch can be removed entirely.
 */
const isPromoRejectionError = (error: unknown): boolean => {
  const { code, message } =
    (error as { code?: string; message?: string } | undefined) ?? {};

  if (code === 'ResourceNotFound' || code?.startsWith('promo.errors.')) {
    return true;
  }

  // `el[ie]gib` matches EN "eligible"/"eligibility" and ES "elegible".
  return code === 'BadRequest' && /\bel[ie]gib/i.test(message ?? '');
};

/**
 * The backend applies a membership discount automatically from the customer's
 * contact details and refuses to let it coexist with a promo. So a member entering a
 * promo code is rejected outright — the code must never reach the breakdown,
 * because the failure would otherwise resurface as an unbookable cart.
 */
const isMembershipPromoConflictError = (error: unknown): boolean => {
  const { code } = (error as { code?: string } | undefined) ?? {};
  return code === MEMBERSHIP_PROMO_CONFLICT_CODE;
};

/**
 * Whether the server rejected the cart specifically because of the
 * promo/gift-card *code* the user entered (SQR-12917). The backend fails the
 * WHOLE cart upsert when a code is invalid — promos throw `promo.errors.*`
 * (400) and gift cards throw `giftCard.errors.*` (e.g. 404 `notFound`). When we
 * see one of these we must drop the bad code from the cached cart, otherwise it
 * is re-spread into every subsequent upsert (`...cachedCart` in
 * `syncCartWithServer`) and the user can never book.
 *
 * A *non-code* cart failure (shop closed, invalid service, network error) is
 * NOT a code rejection, so a valid code the user applied is left untouched.
 */
const isCodeRejectionError = (error: unknown): boolean => {
  const { code } = (error as { code?: string } | undefined) ?? {};
  return (
    code?.startsWith('giftCard.errors.') === true ||
    isMembershipPromoConflictError(error) ||
    isPromoRejectionError(error)
  );
};

/**
 * `promo.errors.generic` says only "Error occurred."; the failing rule's
 * message (e.g. outside the promo's schedule) is in `errors[]`.
 */
const getCartErrorMessage = (error: unknown): string => {
  const { message, errors } =
    (error as
      | { message?: string; errors?: readonly { message?: string }[] }
      | undefined) ?? {};
  return errors?.find((entry) => !!entry?.message)?.message || message || '';
};

// Module-level flag to track checkout state across all hook instances.
// This is necessary because multiple screens (ChooseTimeScreen, ChooseServiceScreen,
// ChooseProfessionalScreen) may have their own hook instances with pending mutations.
// When checkout starts from any screen, we need to suppress error toasts from ALL
// in-flight mutations that complete after the cart has been consumed.
let hasCheckoutStarted = false;

/**
 * Mark that checkout has started. This suppresses error toasts from
 * in-flight mutations that complete after the cart has been consumed.
 * Call this when initiating checkout to prevent "cart not found" errors
 * from being shown to the user.
 *
 * This is a module-level function so it affects all hook instances.
 */
export const markCheckoutStarted = () => {
  hasCheckoutStarted = true;
};

/**
 * Reset the checkout state. Call this when returning to booking flow
 * to re-enable error toasts for new bookings.
 */
export const resetCheckoutState = () => {
  hasCheckoutStarted = false;
};

/**
 * Check if checkout has started. Use this to guard cleanup callbacks
 * (like unfocus handlers) from making mutations after checkout completes.
 */
export const isCheckoutStarted = () => hasCheckoutStarted;

// Promo IDs the server has already rejected for the current customer.
// `promoId` is carried in the URL and threaded through every booking screen,
// so without this it gets re-applied (and re-rejected) on every professional/
// service/time change. Once a promo is rejected we stop re-attaching it across
// all hook instances and screens, and only toast the rejection once.
// Module-level for the same reason as `hasCheckoutStarted`: it must outlive
// individual hook instances. The hook clears it when the customer changes (see
// the customer-id effect) so a rejection for one user never blocks another.
const rejectedPromoIds = new Set<string>();

/** Record a promo the server rejected so it is never re-applied this session. */
export const markPromoRejected = (promoId: string) => {
  rejectedPromoIds.add(promoId);
};

/** Whether the server already rejected this promo this session. */
export const isPromoRejected = (promoId?: string | null): boolean =>
  !!promoId && rejectedPromoIds.has(promoId);

/** Clear rejected-promo tracking (e.g. starting a fresh booking, or in tests). */
export const resetRejectedPromos = () => {
  rejectedPromoIds.clear();
};

/**
 * Contact details the backend fuzzy-matches to a customer on every cart
 * upsert. Until a guest has a customer id, the membership discount and the
 * stamped `item.customerId` come from this match, so it rides along on every
 * call — not just the one that first identifies the guest. `transformCartIntoPayloadCart` handles wire safety
 * (dropping an empty `preferredLanguage`); this only decides who to send and
 * whether they are identifiable at all (SQR-15069).
 */
const buildCartCustomer = (
  source?: {
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    email?: string | null;
    preferredLanguage?: string | null;
  } | null,
): CartCustomerPayload | undefined => {
  if (!source) return undefined;

  // The fuzzy-match needs a name plus one contact field. Details that can't
  // identify anyone is worse than none: it would clear an already-applied
  // discount (and some placeholder customers are entirely blank).
  const isMatchable = !!source.firstName && (!!source.email || !!source.phone);
  if (!isMatchable) return undefined;

  return {
    firstName: source.firstName || '',
    lastName: source.lastName || '',
    phone: source.phone || '',
    email: source.email || '',
    ...(source.preferredLanguage
      ? { preferredLanguage: source.preferredLanguage }
      : {}),
  };
};

const toIdentityKey = (customer?: CartCustomerPayload | null) =>
  customer
    ? [customer.firstName, customer.lastName, customer.email, customer.phone]
        .map((field) => (field ?? '').trim().toLowerCase())
        .join('|')
    : '';

export const useOptimisticCartUpdater = (
  shopId: string,
  cartId?: string,
  services?: Service[],
  // When provided (e.g. from a `promoId` URL param), the promo is reapplied on
  // every cart mutation so deep-linked carts keep the promo applied across
  // professional/service/time changes that would otherwise drop it.
  defaultPromoId?: string,
) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t: errorT } = useErrorTranslations('cart');

  // Track the previous cartId to detect when a new cart is created
  const previousCartIdRef = useRef(cartId);

  const hasPendingLocalEditRef = useRef(false);

  // When true, suppress the error toast for promo-validation failures so
  // createCartWithPromo can transparently retry without the promo. Set
  // before the promo-aware attempt, cleared in finally.
  const suppressPromoErrorToastRef = useRef(false);

  // Reset checkout state when a new cart is created
  // This ensures error toasts work correctly for new booking flows
  useEffect(() => {
    if (cartId && cartId !== previousCartIdRef.current) {
      resetCheckoutState();
      previousCartIdRef.current = cartId;
    }
  }, [cartId]);

  const getCartQueryOptions = getCartQuery(shopId, cartId);

  const { data: customer } = useCustomerQuery();
  const sessionCustomerId = useSessionCustomerId();

  // The customer squire-api matched a guest's details to on validate. Later
  // upserts name that id while the details stay the same (SQR-15987).
  const guestMatchRef = useRef<{ key: string; customerId: string } | null>(
    null,
  );
  const resolveCartCustomerId = (cartCustomer?: CartCustomerPayload) => {
    if (sessionCustomerId) return sessionCustomerId;
    const guestMatch = guestMatchRef.current;
    return guestMatch && guestMatch.key === toIdentityKey(cartCustomer)
      ? guestMatch.customerId
      : undefined;
  };
  const { data: shop } = useQuery(shopDetailQuery(shopId));

  // Rejected-promo tracking is scoped to the current customer. The JS runtime
  // (and thus the module-level Set) outlives a logout/login, so when the
  // customer identity changes we clear it — otherwise a promo rejected for one
  // user could stay blocked for a different user who is actually eligible.
  const previousCustomerIdRef = useRef(customer?.id);
  useEffect(() => {
    if (customer?.id !== previousCustomerIdRef.current) {
      resetRejectedPromos();
      previousCustomerIdRef.current = customer?.id;
    }
  }, [customer?.id]);

  // Typed with a guaranteed language: this value is handed to BNPL and Cash App
  // session storage, which validate the field at runtime. '' means "unknown" —
  // `transformCartIntoPayloadCart` strips it before it reaches the API.
  const [savedCustomer, setSavedCustomer] =
    useState<CartCustomerWithLanguage | null>(null);

  // Store the last payment payload for use in waitlist submissions
  const [savedPayment, setSavedPayment] = useState<PaymentPayload | null>(null);

  const {
    data: queriedCart,
    isLoading: isLoadingCart,
    isFetched: isCartFetched,
    isFetching: isFetchingCart,
  } = useQuery(getCartQueryOptions);

  // Server only returns a resolved `promo` object on POST/PUT cart mutations;
  // GET /v2/shop/:id/cart/:id omits it (only stores `promoId` on the cart).
  // Preserve the last promo we saw from any mutation response so the UI can
  // display the deep-linked promo across screen navigations.
  const lastKnownPromoRef = useRef<Cart['promo'] | undefined>(undefined);
  useEffect(() => {
    if (queriedCart?.promo) {
      lastKnownPromoRef.current = queriedCart.promo;
    }
  }, [queriedCart?.promo]);

  const cart = useMemo(() => {
    if (!queriedCart) return queriedCart;
    if (queriedCart.promo || !lastKnownPromoRef.current) return queriedCart;
    return { ...queriedCart, promo: lastKnownPromoRef.current };
  }, [queriedCart]);

  // Track the last cartId we have observed a non-null cart for. The persisted
  // React Query cache can momentarily write `null` for a still-valid cart
  // (status stays "success", a new dataUpdatedAt, immediately overwritten by
  // the real cart on the next render). Without this guard that single null
  // render flips isCartInvalid true and the booking screens recreate the cart
  // on every refresh, losing in-progress state (SQR-12748).
  const lastValidCartIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (cart && cartId) {
      lastValidCartIdRef.current = cartId;
    }
  }, [cart, cartId]);

  // Cart is invalid when a cartId was provided but the query resolved to null.
  // This happens when the cart has expired, was already consumed, or the ID is
  // bogus. A transient null for a cartId we have already seen a valid cart for
  // is the cache flicker described above, not a real invalidation, so ignore it.
  const isCartInvalid =
    !!cartId && isCartFetched && !cart && lastValidCartIdRef.current !== cartId;

  const upsertCartMutation = useMutation(
    upserCartMutation({
      onMutate: async (variables) => {
        // SQR-15545: payments are not applied optimistically — they must land
        // together with the server-computed breakdown or totals go out of sync.
        const { items, payments: _payments, ...restCart } = variables;

        // Snapshot the codes BEFORE the optimistic write so onError can revert
        // exactly what this attempt changed if the server rejects the code
        // (SQR-12917). Captured here rather than derived from the error because
        // the entered code can live in either `promoCode` or `giftCardCode`
        // depending on cart state, and the backend's gift-card-vs-promo error
        // classification does not map 1:1 to which client field carried it.
        const previousCart = queryClient.getQueryData<Cart>(
          getCartQueryOptions.queryKey,
        );

        queryClient.setQueryData<Cart | null | undefined>(
          getCartQueryOptions.queryKey,
          (old) => {
            const transformedItems = items?.map((cartItem, index) => {
              const correctServices = cartItem.services.map((service) => ({
                id: service.id,
              }));

              // Get existing cached item to preserve server-populated fields
              const existingItem = old?.items?.[index];

              // Cast to access subItems at runtime (TypeScript type is narrower than actual object)
              const existingFull = existingItem as CartItem | undefined;
              const cartItemFull = cartItem as CartItem;

              // Merge items, explicitly preserving server-populated fields
              // These fields come from API responses and can't be generated client-side
              return {
                ...existingItem,
                ...cartItem,
                services: correctServices,
                // Preserve subItems from existing item if payload doesn't have them
                // subItems contains service details (name, cost, duration) from the server
                subItems:
                  cartItemFull.subItems?.length > 0
                    ? cartItemFull.subItems
                    : existingFull?.subItems || [],
                // Preserve barber info unless payload explicitly sets it
                barber: cartItemFull.barber || existingFull?.barber,
                // Preserve other server-populated fields
                availableTips:
                  cartItemFull.availableTips?.length > 0
                    ? cartItemFull.availableTips
                    : existingFull?.availableTips || [],
              } as typeof cartItem;
            });
            return {
              ...old,
              ...restCart,
              items: transformedItems as CartItem[],
            } as Cart;
          },
          { updatedAt: new Date().valueOf() },
        );

        return { previousCart };
      },
      onSuccess: (data, _, __) => {
        const isNewCart = !cartId || cartId !== data.id;

        // The server miscalculates per-item tipAmount for same-barber group
        // appointments (returns a lower total than the per-item availableTips
        // sum). Correct each item's tipAmount using its own availableTips
        // preset, which the server computes correctly. This matches how
        // water-on-web displays tip amounts.
        const correctedItems = data.items.map((item) => {
          if (item.tipPercentage && item.availableTips?.length) {
            const preset = item.availableTips.find(
              (t) => t.percentage === item.tipPercentage,
            );
            if (preset && preset.amount !== item.tipAmount) {
              return { ...item, tipAmount: preset.amount };
            }
          }
          return item;
        });
        const correctedData = { ...data, items: correctedItems };

        // Always seed the cache so the BottomSheet gets server-populated fields
        // (subItems, pricing, etc.) that the optimistic update doesn't include.
        //
        // When other mutations are still in-flight, merge rather than replace:
        // preserve the current cached services array so an intermediate PUT
        // response (e.g. PUT-A completing while PUT-B is still pending) doesn't
        // overwrite the optimistic services that belong to the pending mutation.
        // All other server fields (subItems, cost, promo, etc.) are applied as-is.
        if (!isNewCart && queryClient.isMutating() > 0) {
          const currentCache = queryClient.getQueryData<Cart>([
            'cart',
            shopId,
            data.id,
          ]);
          queryClient.setQueryData(['cart', shopId, data.id], {
            ...correctedData,
            items: correctedItems.map((serverItem, index) => ({
              ...serverItem,
              services:
                currentCache?.items?.[index]?.services ?? serverItem.services,
            })),
          });
        } else {
          // New cart or no concurrent mutations: apply the full server response.
          // Seed cache BEFORE router.setParams so the re-rendered useQuery for
          // the new cartId finds the POST response (with the resolved `promo`
          // object) already in cache and skips the GET — the GET endpoint does
          // not return a resolved promo for empty carts, only POST/PUT do.
          queryClient.setQueryData(['cart', shopId, data.id], correctedData);
        }

        if (isNewCart) {
          router.setParams({ cart: data.id });
        }

        // // Get the cached cart to preserve optimistic item IDs
        // const cachedCart = context.client.getQueryData<Cart>(queryKey);

        // // Merge server data with cached item IDs and services to prevent re-renders
        // // The server returns new item IDs on every upsert, which causes
        // // React components with key={item.id} to unmount and remount
        // const mergedCart = {
        //   ...cachedCart,
        //   ...data,
        //   items: data.items.map((serverItem, index) => {
        //     const cachedItem = cachedCart?.items?.[index];
        //     return {
        //       ...serverItem,
        //       // Preserve the cached item ID if it exists, otherwise use server ID
        //       id: cachedItem?.id || serverItem.id,
        //       // Preserve optimistic services from cache to avoid race conditions
        //       services: cachedItem?.services ?? serverItem.services,
        //     };
        //   }),
        // };

        // context.client.setQueryData(queryKey, mergedCart, {
        //   updatedAt: new Date().valueOf(),
        // });
      },
      onError: (error, variables, onMutateResult) => {
        // In this TanStack v5 the onMutate return value is the 3rd arg
        // (onMutateResult); the 4th is an internal MutationFunctionContext, NOT
        // the onMutate return.
        const mutateContext = onMutateResult as
          | { previousCart?: Cart }
          | undefined;
        // A taken slot or a rejected code is reverted with a toast below.
        const log =
          isNoResponse(error) ||
          isSlotUnavailableError(error) ||
          isCodeRejectionError(error)
            ? Logger.warn
            : Logger.error;
        log('Error updating cart', { error });

        // SQR-12917: when the server rejects the cart because of the
        // promo/gift-card code the user entered, the optimistic onMutate has
        // already written that bad code into the cached cart. If it stays, it is
        // re-spread into every subsequent upsert (`...cachedCart` in
        // syncCartWithServer) and the booking keeps failing — the user is
        // stranded on the stale code.
        //
        // Revert ONLY the code fields to their pre-attempt values
        // (previousCart), leaving items/tips/customer as-is. Reverting from the
        // snapshot — rather than blindly nulling — means a code that was already
        // valid before this attempt survives even though the cart can carry both
        // a promo and a gift card at once. It is also robust to which field
        // (promoCode vs giftCardCode) actually held the rejected value, which
        // the backend's gift-card-vs-promo error classification does not reveal.
        let shouldRetryWithoutPromo = false;
        let deepLinkRejectionMessage: string | undefined;
        if (isCodeRejectionError(error)) {
          const previousCart = mutateContext?.previousCart;
          const attemptChangedTypedCode =
            (variables.promoCode ?? null) !==
              (previousCart?.promoCode ?? null) ||
            (variables.giftCardCode ?? null) !==
              (previousCart?.giftCardCode ?? null);
          const attemptChangedCode =
            attemptChangedTypedCode ||
            (variables.promoId ?? null) !== (previousCart?.promo?.id ?? null);
          // `resolvePromoId` adds `defaultPromoId` to a cart created without it
          // (barber entries), so its first send is not a change the user made.
          // `createCartWithPromo` handles its own rejection (toast ref set).
          const attemptAddedDeepLinkedPromo =
            !suppressPromoErrorToastRef.current &&
            !attemptChangedTypedCode &&
            !previousCart?.promo?.id &&
            !!defaultPromoId &&
            variables.promoId === defaultPromoId;
          // Codes unchanged (e.g. a time change) means the promo on the cart is
          // the rejected one, so it's dropped rather than restored (SQR-10213).
          const shouldDropPromo =
            (!attemptChangedCode || attemptAddedDeepLinkedPromo) &&
            (isPromoRejectionError(error) ||
              isMembershipPromoConflictError(error));
          queryClient.setQueryData<Cart | null | undefined>(
            getCartQueryOptions.queryKey,
            (old) =>
              old
                ? {
                    ...old,
                    promoCode: shouldDropPromo
                      ? null
                      : (previousCart?.promoCode ?? null),
                    giftCardCode: previousCart?.giftCardCode ?? null,
                    promo: shouldDropPromo ? undefined : previousCart?.promo,
                  }
                : old,
          );
          if (shouldDropPromo) {
            // Stops `resolvePromoId` re-sending a deep-linked `defaultPromoId`
            // and `lastKnownPromoRef` re-displaying the promo.
            lastKnownPromoRef.current = undefined;
            const droppedPromoId = previousCart?.promo?.id ?? variables.promoId;
            if (droppedPromoId) {
              if (
                attemptAddedDeepLinkedPromo &&
                !isPromoRejected(droppedPromoId)
              ) {
                deepLinkRejectionMessage = getCartErrorMessage(error);
              }
              markPromoRejected(droppedPromoId);
              if (defaultPromoId === droppedPromoId) {
                router.setParams({ promoId: undefined });
              }
            }
          }
          shouldRetryWithoutPromo =
            shouldDropPromo &&
            (!!variables.promoCode || attemptAddedDeepLinkedPromo);
        } else if (
          isSlotUnavailableError(error) &&
          mutateContext?.previousCart
        ) {
          // Revert only the position(s) this attempt changed — by position, not item id, since the server reassigns ids on every upsert (SQR-13415).
          const snapshot = mutateContext.previousCart;
          const changedIndexes = new Set(
            (variables.items ?? [])
              .map((attempted, index) => {
                const prev = snapshot.items?.[index];
                return prev &&
                  (prev.dateTime !== attempted.dateTime ||
                    prev.barberId !== attempted.barberId)
                  ? index
                  : -1;
              })
              .filter((index) => index !== -1),
          );

          if (changedIndexes.size > 0) {
            queryClient.setQueryData<Cart | null | undefined>(
              getCartQueryOptions.queryKey,
              (current) =>
                current
                  ? {
                      ...current,
                      items: (current.items || []).map((item, index) =>
                        changedIndexes.has(index)
                          ? (snapshot.items?.[index] ?? item)
                          : item,
                      ),
                    }
                  : current,
            );
          }
        }

        // Skip this toast only for the promo-carrying attempt inside
        // createCartWithPromo (suppressPromoErrorToastRef set) AND only when the
        // rejection is actually about the promo — that flow retries without the
        // promo and surfaces the rejection message itself after the retry, so
        // toasting here too would double up. A *genuine* cart failure during
        // that same attempt (e.g. shop closed) is NOT a promo rejection, so it
        // still surfaces here. Also skip if checkout has started, because the
        // cart may have been consumed by a successful checkout while this
        // mutation was in flight.
        // The whole upsert failed, so re-send the change without the promo;
        // only a deep-linked promo's first rejection toasts, as in
        // `createCartWithPromo`.
        if (shouldRetryWithoutPromo && !hasCheckoutStarted) {
          // Variables are spread from the cached cart, so they carry its
          // `promo` too; onMutate would write it back into the cache.
          const { promo: _droppedPromo, ...variablesWithoutPromo } =
            variables as typeof variables & Pick<Cart, 'promo'>;
          upsertCartMutation.mutate({
            ...variablesWithoutPromo,
            promoCode: null,
            promoId: null,
          });
          if (deepLinkRejectionMessage) {
            toast({ variant: 'error', message: deepLinkRejectionMessage });
          }
          return;
        }

        const suppressForPromoRetry =
          suppressPromoErrorToastRef.current && isPromoRejectionError(error);
        if (!hasCheckoutStarted && !suppressForPromoRetry) {
          toast({
            variant: 'error',
            message: isMembershipPromoConflictError(error)
              ? errorT('membershipPromoConflict')
              : getCartErrorMessage(error),
          });
        }
      },
      onSettled: () => {
        // Only invalidate when this is the last settling mutation.
        // While other mutations are in-flight, a refetch would return stale
        // server state and overwrite their optimistic cache updates.
        if (queryClient.isMutating() === 0) {
          queryClient.invalidateQueries({
            queryKey: getCartQueryOptions.queryKey,
          });
        }
      },
    }),
  );

  const getCurrentCartItems = (): Parameters<typeof mutateCart>[0] => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );

    return cachedCart?.items || [];
  };

  /** Deep clones cart items to avoid mutating React Query cache directly */
  const cloneCartItems = () =>
    getCurrentCartItems().map((item) => ({
      ...item,
      services: [...(item.services ?? [])],
    }));

  // Resolves the promoId to send to the server, honoring the same precedence
  // as before (explicit > already on the cart > deep-linked default) but
  // dropping any promo the server has already rejected this session. Without
  // this, the deep-linked promoId is re-applied — and re-rejected — on every
  // professional/service/time change.
  const resolvePromoId = (
    explicitPromoId?: string | null,
    cartPromoId?: string | null,
  ): string | undefined => {
    const resolved = explicitPromoId || cartPromoId || defaultPromoId;
    return isPromoRejected(resolved) ? undefined : resolved || undefined;
  };

  const syncCartWithServer = (
    items: Pick<
      CartItem,
      | 'id'
      | 'name'
      | 'services'
      | 'bookedWithAnyBarber'
      | 'barberId'
      | 'barberPasscode'
      | 'dateTime'
      | 'tipAmount'
      | 'tipPercentage'
    >[],
    newCart?: Partial<UpsertCartPayload>,
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1] & {
      cartId?: string;
    },
  ) => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );

    if (!cachedCart) return;

    try {
      validateCartItems(items, shop);

      queryClient.cancelQueries({
        queryKey: getCartQueryOptions.queryKey,
      });

      const hasGiftCardPayment = newCart?.payments?.some(
        (payment) => payment.type === 'gift_card',
      );

      let _payments = newCart?.payments || [];

      if (!hasGiftCardPayment) {
        _payments = _payments.filter((payment) => payment.type !== 'gift_card');
      }

      // Every upsert must carry the customer, otherwise the backend clears the
      // membership discount and the stamped `item.customerId` — a later tip or
      // time change would silently drop a discount the user has already been
      // shown.
      // A signed-in customer outranks `savedCustomer`: after a guest fills the
      // form and then signs in with a different account, the resync must match
      // on the account, not the stale details they typed (SQR-15069, 21ba00c2).
      const cartCustomer = buildCartCustomer(
        newCart?.customer ?? customer ?? savedCustomer,
      );

      return upsertCartMutation.mutate(
        {
          ...cachedCart,
          ...newCart,
          shopId: shopId,
          customerId: resolveCartCustomerId(cartCustomer),
          cartId: cartId ?? cachedCart?.id,
          items,
          promoId: resolvePromoId(newCart?.promoId, cachedCart?.promo?.id),
          services: services || [],
          payments: _payments,
          customer: cartCustomer,
        },
        options,
      );
    } catch (error) {
      Logger.error('Cart is not valid', { error });
    }
  };

  // When the customer identity changes mid-checkout (guest → signed in, or an
  // account switch), a cart built under the previous identity is still
  // associated with it server-side, so customer-specific pricing — notably the
  // membership discount — has not been applied. Re-upsert so the backend
  // recomputes it, instead of waiting for the next cart mutation (changing a
  // time/service) to incidentally resync (SQR-15069).
  //
  // Guarded on payingCustomer so an already-associated cart does NOT trigger a
  // redundant upsert. syncCartWithServer sends
  // customerId + the customer details and no-ops when there is no cart.
  // The ref guard keys the actual work to a customer-id transition, so the
  // effect re-running on other dep changes is a cheap no-op.
  //
  // While the cart is still being created it has no id, and upserting it would
  // POST a second cart. The customer transition stays pending until the cart id
  // arrives, then resyncs once (SQR-15722).
  const syncCartWithServerRef = useRef(syncCartWithServer);
  useEffect(() => {
    syncCartWithServerRef.current = syncCartWithServer;
  });

  const resyncedCustomerIdRef = useRef(sessionCustomerId);
  useEffect(() => {
    if (sessionCustomerId === resyncedCustomerIdRef.current) return;

    if (!sessionCustomerId) {
      resyncedCustomerIdRef.current = sessionCustomerId;
      return;
    }

    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );
    if (!cachedCart?.id) return;

    resyncedCustomerIdRef.current = sessionCustomerId;
    if (cachedCart.payingCustomer?.id !== sessionCustomerId) {
      syncCartWithServerRef.current(cachedCart.items ?? [], {});
    }
  }, [sessionCustomerId, cart?.id, queryClient, getCartQueryOptions]);

  const getCartPayload = (
    restCart?: Partial<Omit<UpsertCartPayload, 'items'>>,
  ) => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );

    if (!cachedCart) return null;

    return {
      ...cachedCart,
      ...restCart,
      shopId: shopId,
      customerId: resolveCartCustomerId(buildCartCustomer(savedCustomer)),
      cartId: cartId ?? cachedCart?.id,
      items: cachedCart?.items || [],
      // Include the shop's services list so transformCartIntoPayloadCart can
      // convert parent IDs back to real service IDs. Without this, the server
      // receives parent IDs it can't resolve, returning items with empty subItems.
      services: services || [],
      promoCode: restCart?.promoCode || cachedCart?.promoCode || null,
      promoId: resolvePromoId(restCart?.promoId, cachedCart.promo?.id),
    };
  };

  const syncCartForWaitlist = async () => {
    try {
      const payload = getCartPayload();
      if (!payload) return;
      return await upsertCartMutation.mutateAsync(payload);
    } catch (error) {
      Logger.error('Failed to sync cart for waitlist', { error });
    }
  };

  const mutateCart = (
    items: Pick<
      CartItem,
      | 'id'
      | 'name'
      | 'services'
      | 'bookedWithAnyBarber'
      | 'barberId'
      | 'barberPasscode'
      | 'dateTime'
      | 'tipAmount'
      | 'tipPercentage'
      | 'bookedFromWaitingList'
    >[],
    restCart?: Partial<Omit<UpsertCartPayload, 'items'>>,
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1] & {
      cartId?: string;
    },
  ) => {
    if (!shopId) return;

    return syncCartWithServer(items, { ...restCart }, options);
  };

  const createCart = async () => {
    return upsertCartMutation.mutateAsync({
      items: [],
      shopId: shopId,
      services: services || [],
    });
  };

  /**
   * Atomically creates a new cart with an optional promo code/ID.
   * This prevents race conditions that occur when calling createCart() and setPromoId() sequentially.
   *
   * For signed-in users the payload includes an empty appointment shell
   * carrying `customerId`, mirroring the WOW deep-link cart shape. The
   * shell is required to satisfy the backend's promo validation
   * (`matchCustomerFuzzy`) for authenticated requests — without it the
   * server returns `promo.errors.noCustomer` on empty deep-linked carts.
   *
   * If the server rejects the promo (already redeemed, expired, not-found,
   * etc.) we silently retry without the promo so the user can still book.
   * The promo-rejection toast is suppressed; a cart-without-promo is
   * preferable to a stuck skeleton on Choose Professional.
   */
  const createCartWithPromo = async (promo?: {
    promoId?: string | null;
    promoCode?: string | null;
  }) => {
    const items = sessionCustomerId
      ? [
          {
            id: '',
            name: '',
            bookedWithAnyBarber: false,
            bookedFromWaitingList: false,
            barberId: null,
            barberPasscode: undefined,
            dateTime: null,
            services: [],
            tipAmount: 0,
            tipPercentage: null,
          },
        ]
      : [];

    const payload = {
      items,
      shopId: shopId,
      customerId: sessionCustomerId,
      services: services || [],
    };

    if (!promo?.promoId && !promo?.promoCode) {
      return upsertCartMutation.mutateAsync(payload);
    }

    suppressPromoErrorToastRef.current = true;
    try {
      return await upsertCartMutation.mutateAsync({
        ...payload,
        promoId: promo?.promoId ?? undefined,
        promoCode: promo?.promoCode ?? undefined,
      });
    } catch (error) {
      // Only treat this as a promo problem if the rejection is actually about
      // the promo (typed `promo.errors.*`/`ResourceNotFound` code, or the
      // generic `BadRequest` the referral-eligibility check throws — detected
      // via message). A genuine cart failure (closed shop, invalid service,
      // network error) must NOT be masked as a promo rejection, so we re-throw
      // it and let the mutation's onError surface the real error toast.
      if (!isPromoRejectionError(error)) {
        throw error;
      }

      // The promo can't be applied (ineligible, expired, already redeemed,
      // not-found, etc.). Previously a generic `BadRequest` slipped through and
      // stranded the user on an infinite Choose Professional skeleton. Retry
      // once without the promo so the user can still book, surfacing the
      // server's own message so they know why the promo was dropped. The retry
      // runs with toast suppression cleared, so if it fails for its own reason
      // that error surfaces normally instead.
      // The api-client rejects with the raw response body
      // ({ code, message }), which is a plain object — NOT an Error instance —
      // so read `message` off the object directly rather than via instanceof.
      const { code, message } =
        (error as { code?: string; message?: string } | undefined) ?? {};
      Logger.console(
        'Promo rejected by server, retrying cart create without it',
        { code },
      );

      // Record this promo so it is never re-applied on later cart changes
      // (it is otherwise re-read from the URL on every professional/service/
      // time selection) and only toast the rejection the first time.
      const rejectedId = promo?.promoId ?? undefined;
      const alreadyRejected = isPromoRejected(rejectedId);
      if (rejectedId) {
        markPromoRejected(rejectedId);
        // Drop the dead promo from the URL so it isn't carried forward in
        // navigation params or shared in a link. Pass `undefined` (not '') to
        // remove the query key entirely — matching how the codebase clears the
        // `cart` param — otherwise a dangling `?promoId=` is left behind. The
        // rejected-promo Set is the source of truth; this just keeps the
        // address bar honest.
        if (defaultPromoId === rejectedId) {
          router.setParams({ promoId: undefined });
        }
      }

      suppressPromoErrorToastRef.current = false;
      const cartWithoutPromo = await upsertCartMutation.mutateAsync(payload);
      // Toast the server's message the first time only. Skip if it's empty so
      // we never render a contentless toast (just the "!" icon) — the user
      // still gets the working cart without the promo.
      if (!alreadyRejected && message) {
        toast({
          variant: 'error',
          message,
        });
      }
      return cartWithoutPromo;
    } finally {
      suppressPromoErrorToastRef.current = false;
    }
  };

  // Adds an empty guest slot to the cart for group appointments. This updates
  // ONLY the client cache — it does NOT upsert to the server. An empty
  // appointment (no barber, no services) isn't a persistable entry, and the
  // caller (handleAddAppointment) navigates away immediately; a server PUT here
  // would be cancelled mid-flight on single-professional shops, reverting the
  // optimistic slot and letting the guest's later selections overwrite the
  // primary appointment (SQR-12859). The slot is persisted on the next real
  // mutation, when the guest's barber is chosen. Mirrors water-on-web, which
  // keeps the new reservation in client state until it is completed.
  const addEmptyCartItem = (barberId?: string) => {
    // The empty slot is a partial placeholder, not a full CartItem — it has no
    // barber, services, pricing, etc. yet. Type it against the exact subset the
    // upsert path (syncCartWithServer) accepts so the 10 fields below are still
    // structurally validated at this declaration; the remaining CartItem fields
    // are populated on the next real mutation when the guest's barber/services
    // are chosen.
    // For rental shops, barberId is pre-assigned to enforce same-barber group
    // appointments (matching water-on-web behavior).
    const emptyItem: EmptyCartItem = {
      id: '',
      name: '',
      bookedWithAnyBarber: false,
      bookedFromWaitingList: false,
      barberId: barberId ?? null,
      barberPasscode: undefined,
      dateTime: null,
      services: [],
      tipAmount: 0,
      tipPercentage: null,
    };

    queryClient.setQueryData<Cart | null | undefined>(
      getCartQueryOptions.queryKey,
      (old) => {
        if (!old) {
          Logger.error('useOptimisticCartUpdater: cache miss for cart', {
            shopId,
            cartId,
          });
          return old;
        }
        return {
          ...old,
          // Cart.items is CartItem[]; the placeholder is intentionally partial
          // until the guest's selections are made, so this single element is
          // bridged to CartItem here rather than fabricating phantom fields.
          items: [...(old.items || []), emptyItem as CartItem],
        };
      },
    );
  };

  const removeLastCartItem = () => {
    const items = cloneCartItems();
    items.pop();

    return mutateCart(items);
  };

  const setProfessional = (
    barber: SelectedProfessional | null,
    passcode?: string,
    restCart?: Partial<Omit<UpsertCartPayload, 'items'>>,
  ) => {
    const items = cloneCartItems();

    let item = items.at(-1);

    if (item) {
      item.dateTime = null;
      item.services = [];

      if (barber?.type === 'specific') {
        item.barberId = barber.barberId;
        item.bookedWithAnyBarber = false;
        item.barberPasscode = passcode;
      } else if (barber?.type === 'any') {
        item.barberId = '';
        item.bookedWithAnyBarber = true;
        item.barberPasscode = undefined;
      } else {
        item.barberId = '';
        item.bookedWithAnyBarber = false;
        item.barberPasscode = undefined;
      }
    } else {
      item = {
        id: '',
        name: '',
        bookedWithAnyBarber: barber?.type === 'any',
        bookedFromWaitingList: false,
        barberId: barber?.type !== 'any' ? barber?.barberId || null : null,
        barberPasscode: barber?.type === 'specific' ? passcode : undefined,
        dateTime: null,
        services: [],
        tipAmount: 0,
        tipPercentage: null,
      };

      items.push(item as CartItem);
    }

    return mutateCart(items, restCart);
  };

  const addService = async (
    barberId: string | undefined,
    service: SelectedService,
  ) => {
    const items = cloneCartItems();

    if (!items.length) {
      items.push({
        id: '',
        name: '',
        bookedWithAnyBarber: !barberId,
        bookedFromWaitingList: false,
        barberId: barberId ?? '',
        dateTime: null,
        services: [],
        tipAmount: 0,
        tipPercentage: null,
      });
    }

    const item = items.at(-1);

    if (!item) return;

    const serviceExists = item?.services?.some((s) => s.id === service.id);

    if (serviceExists || !item) return;

    item.dateTime = null;
    item.services = [...(item?.services || []), { id: service.id }];

    Logger.console('Adding service', {
      services: item.services,
    });
    return mutateCart(items);
  };

  const removeService = (serviceId: string) => {
    const items = cloneCartItems();

    const item = items.at(-1);

    if (!item) return;

    item.dateTime = null;
    item.services = item.services.filter((s) => s.id !== serviceId);

    return mutateCart(items);
  };

  const removeServiceLocal = (serviceId: string, appointmentId?: string) => {
    queryClient.setQueryData<Cart | null | undefined>(
      getCartQueryOptions.queryKey,
      (old) => {
        if (!old?.items?.length) return old;
        const items = [...old.items];
        // Target the edited appointment by id so the trash button can't mutate
        // a different appointment than the one shown. Fall back to the last
        // item to preserve behavior when no id is supplied.
        const targetIndex = appointmentId
          ? items.findIndex((it) => it.id === appointmentId)
          : items.length - 1;
        if (targetIndex === -1) return old;
        const item = { ...items[targetIndex] };
        const subItems = item.subItems ?? [];

        const removeIndex = subItems.findIndex(
          (sub) => sub.referenceId === serviceId || sub.id === serviceId,
        );
        if (removeIndex === -1) return old;

        item.subItems = subItems.filter((_, i) => i !== removeIndex);
        item.services =
          item.services.length === subItems.length
            ? item.services.filter((_, i) => i !== removeIndex)
            : item.services.filter((s) => s.id !== serviceId);

        items[targetIndex] = item;
        return { ...old, items };
      },
    );

    hasPendingLocalEditRef.current = true;
  };

  const commitCart = (
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    if (!hasPendingLocalEditRef.current) return;
    return mutateCart(getCurrentCartItems(), undefined, {
      ...options,
      onSuccess: (data, variables, onMutateResult, context) => {
        hasPendingLocalEditRef.current = false;

        const correctedItems = getCurrentCartItems();
        const tipNeedsResync = correctedItems.some(
          (item, index) =>
            (item.tipAmount ?? 0) !==
            (variables.items?.[index]?.tipAmount ?? 0),
        );
        if (tipNeedsResync) {
          setTimeout(() => mutateCart(correctedItems), 0);
        }

        options?.onSuccess?.(data, variables, onMutateResult, context);
      },
    });
  };

  const clearServices = () => {
    const items = cloneCartItems();

    const item = items.at(-1);

    if (!item) return;

    item.services = [];
    item.dateTime = null;

    return mutateCart(items);
  };

  const setAppointmentTime = (
    appointmentId: string,
    time: Date,
    barberId?: string,
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    const items = cloneCartItems();
    const itemIndex = items.findIndex((item) => item.id === appointmentId);

    if (itemIndex === -1) return;

    const item = items[itemIndex];

    // Convert TZDate to UTC ISO string for consistency with backend
    // TZDate.toISOString() produces timezone-offset format (e.g., "2025-12-20T10:00:00.000-05:00")
    // We need UTC format (e.g., "2025-12-20T15:00:00.000Z")
    item.dateTime = new Date(time.getTime()).toISOString();

    // For "any barber" bookings, set the barberId from the selected time slot
    // The backend requires a barberId even when bookedWithAnyBarber is true
    if (barberId && item.bookedWithAnyBarber) {
      item.barberId = barberId;

      // Look up barber info from the professionals cache for optimistic UI update
      const professionalsQueryKey = getProfessionalsQuery(shopId).queryKey;
      const professionals = queryClient.getQueryData<ProfessionalsResponse>(
        professionalsQueryKey,
      );
      const professional = professionals?.find((p) => p.barber.id === barberId);
      if (professional) {
        // Set the barber info for immediate UI update
        // Cast through unknown since Professional.barber has fewer properties than CartItem.barber
        // but contains the essential fields needed for UI display (firstName, lastName, photos)
        (item as CartItem).barber = professional.barber as CartItem['barber'];
      }
    }

    Logger.console('Setting appointment time', {
      services: item.services,
      barberId: item.barberId,
    });

    return mutateCart(items, undefined, options);
  };

  const clearAppointmentTime = (appointmentId: string) => {
    const items = cloneCartItems();
    const itemIndex = items.findIndex((item) => item.id === appointmentId);

    if (itemIndex === -1) return;

    const item = items[itemIndex];
    item.dateTime = '';

    // Always clear tips when clearing appointment time
    // Tips are set on the Choose Time screen, so they should be cleared when navigating back
    item.tipAmount = 0;
    item.tipPercentage = null;

    // For "any barber" bookings, also clear the assigned barber
    // This resets the cart item to its state before time selection
    if (item.bookedWithAnyBarber) {
      item.barberId = '';
      (item as CartItem).barber = undefined;
    }

    return mutateCart(items);
  };

  /**
   * Clear all appointment times and tips in a single mutation.
   * This should be used when navigating away from the Choose Time screen
   * to avoid race conditions from multiple parallel mutations.
   */
  const clearAllAppointmentTimes = () => {
    const items = cloneCartItems();

    if (items.length === 0) return;

    items.forEach((item) => {
      item.dateTime = '';
      item.tipAmount = 0;
      item.tipPercentage = null;

      // For "any barber" bookings, also clear the assigned barber
      if (item.bookedWithAnyBarber) {
        item.barberId = '';
        (item as CartItem).barber = undefined;
      }
    });

    return mutateCart(items);
  };

  const setTip = (
    barberId: string,
    tip?: Tip | { amount: number; isCustom: true },
  ) => {
    Tracker.track('tip_selected_before_checkout', {
      tip_amount: String(tip?.amount ?? 0),
      tip_percentage: String(tip && 'percentage' in tip ? tip.percentage : 0),
      is_custom: String(tip ? 'isCustom' in tip && tip.isCustom : false),
    });

    const allItems = getCurrentCartItems();
    const isFirstSameBarber = new Set<string>();

    const updatedItems = allItems.reduce(
      (acc, item) => {
        if (item.barberId === barberId) {
          const isFirst = !isFirstSameBarber.has(barberId);
          if (isFirst) isFirstSameBarber.add(barberId);

          if (!tip) {
            // Reset all same-barber items
            item.tipAmount = 0;
            item.tipPercentage = null;
          } else if ('isCustom' in tip && tip.isCustom) {
            // Custom amount: only apply to first same-barber item, reset others
            if (isFirst) {
              item.tipAmount = tip.amount;
              item.tipPercentage = null;
            } else {
              item.tipAmount = 0;
              item.tipPercentage = null;
            }
          } else if ('percentage' in tip) {
            // Percentage: find matching preset in each item's own availableTips
            // so each item gets its proportional amount based on its service cost
            const matchingPreset = (item as CartItem).availableTips?.find(
              (t) => t.percentage === tip.percentage,
            );
            item.tipAmount = matchingPreset?.amount ?? tip.amount;
            item.tipPercentage = tip.percentage;
          }
        }

        return [...acc, item];
      },
      [] as ReturnType<typeof getCurrentCartItems>,
    );

    return mutateCart(updatedItems);
  };

  const setDefaultTip = (appointmentId: string) => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );
    const cartItem = cachedCart?.items.find(
      (item) => item.id === appointmentId,
    );
    const defaultTip = cartItem?.defaultTip;

    if (!defaultTip?.amount) return;

    const updatedItems = getCurrentCartItems().reduce(
      (acc, item) => {
        if (item.id === appointmentId) {
          // Use the item's own availableTips preset to get the correct amount
          // for this percentage, rather than defaultTip.amount which may be
          // miscalculated for same-barber group appointments.
          const matchingPreset =
            defaultTip.percentage && (item as CartItem).availableTips?.length
              ? (item as CartItem).availableTips.find(
                  (t) => t.percentage === defaultTip.percentage,
                )
              : undefined;
          item.tipAmount = matchingPreset?.amount ?? defaultTip.amount;
          item.tipPercentage = defaultTip.percentage ?? null;
        }
        return [...acc, item];
      },
      [] as ReturnType<typeof getCurrentCartItems>,
    );

    return mutateCart(updatedItems);
  };

  const clearCart = () => {
    const queryKey = getCartQueryOptions.queryKey.slice(0, -1);
    Logger.console('clearing cart', { queryKey });

    queryClient.removeQueries({
      queryKey,
      exact: false,
    });
  };

  /**
   * Applies a promo or gift-card code. `customer` lets a caller identify a guest
   * on this upsert, so the backend can reject a promo that collides with their
   * membership discount here rather than at checkout (SQR-15069).
   */
  const setPromoCode = (
    code: string | null,
    customerForCode?: CartCustomerPayload,
  ) => {
    const existingCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );

    const isGiftCard = !!(code && existingCart?.promoCode);

    const newCart = isGiftCard
      ? {
          giftCardCode: code,
          promoCode: existingCart.promoCode,
        }
      : {
          promoCode: code,
          giftCardCode: existingCart?.giftCardCode || null,
        };

    if (isGiftCard) {
      Tracker.track('gift_card_applied', { gift_card_code: code });
    }

    return mutateCart(getCurrentCartItems(), {
      ...newCart,
      // Only a promo can collide with a membership discount, and the gift-card
      // branch above re-sends the existing promo — identifying the customer here
      // would get the *gift card* rejected for compounding.
      ...(customerForCode && !isGiftCard ? { customer: customerForCode } : {}),
    });
  };

  const removeGiftCardCode = () => {
    Tracker.track('gift_card_removed', {});
    return mutateCart(getCurrentCartItems(), { giftCardCode: null });
  };

  const setCustomer = (_customer: CartCustomerPayload) => {
    // Only use the required customer fields from _customer, don't spread the full customer object
    // to avoid including extraneous fields like timezone, googleId, isDemo that the API rejects
    const _savedCustomer: CartCustomerWithLanguage = {
      firstName: _customer.firstName,
      lastName: _customer.lastName,
      phone: _customer.phone,
      email: _customer.email,
      preferredLanguage: _customer.preferredLanguage ?? '',
    };

    setSavedCustomer(_savedCustomer);
    return mutateCart(getCurrentCartItems(), {
      customer: _savedCustomer,
    });
  };

  /**
   * Ask the server what this guest's cart looks like, without mutating it. The
   * response is the source of truth for the whole summary — it applies the
   * membership discount, re-sizes gift-card payments, and recomputes the total
   * (SQR-15069).
   */
  const validateGuestMembership = async (
    guestCustomer: CartCustomerPayload,
  ): Promise<UpsertCartResponse | null> => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );
    if (!cachedCart) return null;

    try {
      const payload = transformCartIntoPayloadCart(
        { ...cachedCart, customer: guestCustomer },
        undefined,
        services,
      );
      const validatedCart = await validateSale(shopId, {
        ...payload,
        // The fuzzy-match ignores the slot, but a stale one fails the whole
        // validate (400 nonMatchingHours). The real slot is checked at checkout.
        new_appointments: payload.new_appointments.map((appointment) => ({
          ...appointment,
          dateTime: null,
        })),
      });
      const matchedCustomerId = validatedCart.payingCustomer?.id;
      guestMatchRef.current = matchedCustomerId
        ? { key: toIdentityKey(guestCustomer), customerId: matchedCustomerId }
        : null;
      return validatedCart;
    } catch (error) {
      const { code } = (error as { code?: string } | undefined) ?? {};
      // A promo on the cart makes the membership discount impossible by design,
      // so there is simply nothing to preview — not a failure worth logging.
      if (code !== MEMBERSHIP_PROMO_CONFLICT_CODE) {
        Logger.error('Guest cart validation failed', { error });
      }
      return null;
    }
  };

  const addPayment = (
    payment: PaymentPayload | null,
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    // Server-shape card payments (token/cardInfo) are rejected when echoed;
    // only gift cards ride along with the new payment or survive its removal
    const giftCardPayments = (cart?.payments ?? []).filter(
      (existing) => existing.type === 'gift_card',
    );
    setSavedPayment(payment); // Store for waitlist
    return mutateCart(
      getCurrentCartItems(),
      { payments: payment ? [...giftCardPayments, payment] : giftCardPayments },
      options,
    );
  };

  /**
   * Re-upsert the current cart with no payments, then refresh BNPL eligibility.
   *
   * SQR-12809 workaround: a failed/cancelled BNPL payment leaves a stale payment
   * on the cart server-side (its amount was set to the full outstanding total and
   * never reset), so `paymentAmountOutstanding()` drops below the BNPL minimum and
   * the backend reports BNPL ineligible. Sending `payments: []` makes the backend
   * rebuild the cart with no payments, restoring the outstanding total and BNPL
   * eligibility.
   *
   * The re-upsert alone is not enough: `usePaymentSessionQuery` is keyed only by
   * `['payment', 'session', cartId]`, and the cartId is unchanged across the BNPL
   * return, so the query keeps serving the `eligible: false` it cached during the
   * failed payment. We therefore invalidate that query so `useBnplEligibility`
   * re-fetches the now-correct eligibility and the open Confirm sheet re-renders
   * with BNPL.
   *
   * Crucially the invalidation must run in the upsert's `onSuccess` — i.e. AFTER
   * the PUT has actually cleared the payment server-side. `syncCartWithServer`
   * uses the fire-and-forget `mutate`, so invalidating right after the call would
   * race ahead of the PUT and re-fetch (and re-cache) the stale `eligible: false`
   * while the payment is still on the cart.
   *
   * Remove once SQR-12810 fixes the backend fail/cancel path.
   */
  const clearPayments = (
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    const cachedCart = queryClient.getQueryData<Cart>(
      getCartQueryOptions.queryKey,
    );
    const resolvedCartId = cartId ?? cachedCart?.id;

    return mutateCart(
      getCurrentCartItems(),
      { payments: [] },
      {
        ...options,
        onSuccess: (data, variables, onMutateResult, context) => {
          options?.onSuccess?.(data, variables, onMutateResult, context);
          if (resolvedCartId) {
            queryClient.invalidateQueries({
              queryKey: ['payment', 'session', resolvedCartId],
            });
          }
        },
      },
    );
  };

  const addPaymentAndCustomer = (
    payment: PaymentPayload | null,
    _customer: CartCustomerPayload,
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    // Only use the required customer fields from _customer, don't spread the full customer object
    // to avoid including extraneous fields like timezone, googleId, isDemo that the API rejects
    const _savedCustomer: CartCustomerWithLanguage = {
      firstName: _customer.firstName,
      lastName: _customer.lastName,
      phone: _customer.phone,
      email: _customer.email,
      preferredLanguage: _customer.preferredLanguage ?? '',
    };
    setSavedCustomer(_savedCustomer);
    setSavedPayment(payment); // Store for waitlist
    return mutateCart(
      getCurrentCartItems(),
      { customer: _savedCustomer, payments: payment ? [payment] : [] },
      options,
    );
  };

  const getCustomer = (): CartCustomerWithLanguage => {
    return (
      savedCustomer || {
        firstName: customer?.firstName || '',
        lastName: customer?.lastName || '',
        phone: customer?.phone || '',
        email: customer?.email || '',
        preferredLanguage: customer?.preferredLanguage || '',
      }
    );
  };

  const setPromoId = (promoId: string | null) => {
    return mutateCart(getCurrentCartItems(), { promoId });
  };

  const setTimeAndTip = (
    appointmentId: string,
    time: Date,
    barberId?: string,
    tip?: Tip | { amount: number; isCustom: true },
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    const items = cloneCartItems();
    const itemIndex = items.findIndex((item) => item.id === appointmentId);

    if (itemIndex === -1) return;

    const item = items[itemIndex];

    // Convert TZDate to UTC ISO string for consistency with backend
    // TZDate.toISOString() produces timezone-offset format (e.g., "2025-12-20T10:00:00.000-05:00")
    // We need UTC format (e.g., "2025-12-20T15:00:00.000Z")
    item.dateTime = new Date(time.getTime()).toISOString();

    // For "any barber" bookings, set the barberId from the selected time slot
    // The backend requires a barberId even when bookedWithAnyBarber is true
    if (barberId && item.bookedWithAnyBarber) {
      item.barberId = barberId;

      // Look up barber info from the professionals cache for optimistic UI update
      const professionalsQueryKey = getProfessionalsQuery(shopId).queryKey;
      const professionals = queryClient.getQueryData<ProfessionalsResponse>(
        professionalsQueryKey,
      );
      const professional = professionals?.find((p) => p.barber.id === barberId);
      if (professional) {
        // Set the barber info for immediate UI update
        // Cast through unknown since Professional.barber has fewer properties than CartItem.barber
        // but contains the essential fields needed for UI display (firstName, lastName, photos)
        (item as CartItem).barber = professional.barber as CartItem['barber'];
      }
    }

    Logger.console('Setting appointment time', {
      services: item.services,
      barberId: item.barberId,
    });

    const updatedItems = items.reduce(
      (acc, currentItem) => {
        if (currentItem.id === appointmentId) {
          if (!tip) {
            currentItem.tipAmount = 0;
            currentItem.tipPercentage = null;
          } else if ('isCustom' in tip && tip.isCustom) {
            currentItem.tipAmount = tip.amount;
            currentItem.tipPercentage = null;
          } else if ('percentage' in tip) {
            // Use the item's own availableTips preset to get the correct amount,
            // rather than the default tip amount which may be miscalculated for
            // same-barber group appointments.
            const matchingPreset = (
              currentItem as CartItem
            ).availableTips?.find((t) => t.percentage === tip.percentage);
            currentItem.tipAmount = matchingPreset?.amount ?? tip.amount;
            currentItem.tipPercentage = tip.percentage;
          }
        }

        return [...acc, currentItem];
      },
      [] as ReturnType<typeof getCurrentCartItems>,
    );

    return mutateCart(updatedItems, undefined, options);
  };

  /**
   * Atomically clears all appointment times and sets a new time for the specified appointment.
   * This prevents race conditions that occur when calling clearAllAppointmentTimes() and
   * setAppointmentTime() sequentially (e.g., when changing dates or auto-selecting time).
   */
  const clearTimesAndSetTime = (
    appointmentId: string,
    time: Date,
    barberId?: string,
    tip?: Tip | { amount: number; isCustom: true },
    options?: Parameters<typeof upsertCartMutation.mutateAsync>[1],
  ) => {
    const items = cloneCartItems();

    if (items.length === 0) return;

    // Clear all appointment times first (same logic as clearAllAppointmentTimes)
    items.forEach((item) => {
      item.dateTime = '';
      item.tipAmount = 0;
      item.tipPercentage = null;

      // For "any barber" bookings, also clear the assigned barber
      if (item.bookedWithAnyBarber) {
        item.barberId = '';
        (item as CartItem).barber = undefined;
      }
    });

    // Now set the time for the specified appointment
    const targetItem = items.find((item) => item.id === appointmentId);

    if (!targetItem) return;

    // Convert TZDate to UTC ISO string for consistency with backend
    targetItem.dateTime = new Date(time.getTime()).toISOString();

    // For "any barber" bookings, set the barberId from the selected time slot
    if (barberId && targetItem.bookedWithAnyBarber) {
      targetItem.barberId = barberId;

      // Look up barber info from the professionals cache for optimistic UI update
      const professionalsQueryKey = getProfessionalsQuery(shopId).queryKey;
      const professionals = queryClient.getQueryData<ProfessionalsResponse>(
        professionalsQueryKey,
      );
      const professional = professionals?.find((p) => p.barber.id === barberId);
      if (professional) {
        (targetItem as CartItem).barber =
          professional.barber as CartItem['barber'];
      }
    }

    // Apply tip if provided
    if (tip) {
      if ('isCustom' in tip && tip.isCustom) {
        targetItem.tipAmount = tip.amount;
        targetItem.tipPercentage = null;
      } else if ('percentage' in tip) {
        const matchingPreset = (targetItem as CartItem).availableTips?.find(
          (t) => t.percentage === tip.percentage,
        );
        targetItem.tipAmount = matchingPreset?.amount ?? tip.amount;
        targetItem.tipPercentage = tip.percentage;
      }
    }

    Logger.console('Clearing all times and setting new time', {
      appointmentId,
      time: targetItem.dateTime,
      barberId: targetItem.barberId,
    });

    return mutateCart(items, undefined, options);
  };

  const createCartWithProfessional = async (
    barber: SelectedProfessional,
    serviceIds: string[] = [],
  ) => {
    const item: CartItem = {
      id: '',
      name: '',
      bookedWithAnyBarber: barber?.type === 'any',
      barberId: barber?.type !== 'any' ? barber?.barberId || null : null,
      dateTime: null,
      services: serviceIds.map((id) => ({ id })),
      tipAmount: 0,
      tipPercentage: null,
      taxAmount: 0,
      quantity: 1,
      type: 'appointment',
      customerId: null,
      taxId: null,
      priceWithoutTaxes: 0,
      priceWithTaxes: 0,
      parentId: null,
      subItems: [],
      discountAmount: 0,
      promoAmount: 0,
      bookedFromWaitingList: false,
      supplyPrice: 0,
      cost: 0,
      taxBeforeAdjustments: 0,
      availableTips: [],
      useFlatTips: false,
      defaultTip: { percentage: 0, amount: 0, flatAmount: 0 },
      depositAmount: 0,
      requiresDeposit: false,
      requiresPrepaid: false,
      appointment: {
        confirmationCode: '',
      },
    };

    const items = [item];

    return upsertCartMutation.mutateAsync({
      items,
      shopId: shopId,
      customerId: sessionCustomerId,
      services: services || [],
    });
  };

  const changeService = (
    barberId: string | undefined,
    service: SelectedService,
  ) => {
    const items = cloneCartItems();

    if (!items.length) {
      items.push({
        id: '',
        name: '',
        bookedWithAnyBarber: !barberId,
        barberId: barberId ?? '',
        dateTime: null,
        services: [],
        tipAmount: 0,
        tipPercentage: null,
        bookedFromWaitingList: false,
      });
    }

    const item = items.at(-1);

    if (!item) return;

    item.services = [];
    item.dateTime = null;

    const serviceExists = item?.services?.some((s) => s.id === service.id);

    if (serviceExists || !item) return;

    item.dateTime = null;
    item.services = [...(item?.services || []), { id: service.id }];

    Logger.console('Adding service', {
      services: item.services,
    });
    return mutateCart(items);
  };

  return {
    cart,
    isCartInvalid,
    createCart,
    createCartWithPromo,
    addEmptyCartItem,
    removeLastCartItem,
    setProfessional,
    setPromoCode,
    addService,
    removeService,
    removeServiceLocal,
    commitCart,
    clearServices,
    setAppointmentTime,
    clearAppointmentTime,
    clearAllAppointmentTimes,
    clearTimesAndSetTime,
    setTip,
    setDefaultTip,
    setTimeAndTip,
    clearCart,
    isMutatingCart: upsertCartMutation.isPending,
    isLoadingCart,
    isFetchingCart,
    setPromoId,
    setCustomer,
    validateGuestMembership,
    addPayment,
    clearPayments,
    addPaymentAndCustomer,
    syncCartForWaitlist,
    customer: savedCustomer || getCustomer(),
    setLocalCustomer: setSavedCustomer,
    createCartWithProfessional,
    changeService,
    removeGiftCardCode,
    savedPayment,
  };
};
